/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : DiscardChangesWarningModal.tsx
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Account selection popup
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422335
 *
 * @Date         : Feb 23, 2022
 *
 * @Version      : Version 1.0
 *
 * By            : <Author>
 * On            : <Date>
 * Description   : <Change Description>
 *
 *
 ******************************************************************************/

import React, { FC, useState, ReactElement, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Grid } from "@scuf/common";
import {
  setCurrentWorkFlowType,
  setCurrentWorkFlowStep,
  resetSelectedApmRecord,
} from "stores/ApmWorkFlowStore";
import { resetGeneralInfoForm } from "stores/GeneralInfoStore";
import { resetSelectedApmFile } from "stores/SelectApmFileStore";
import { resetSelectedAircraftDetails } from "stores/AircraftDetailsStore";
import { resetAPMSettingsStore } from "stores/ApmSettingsStore";

import {
  setShowAccountModal,
  setDiscardChangesWarning,
  selectDiscardChangesWarning,
} from "stores/UIStore";

import {
  EnumHeaderAction,
  EnumApmSettingsWorkFlow,
} from "models/configuration";

export const DiscardChangesWarningModal: FC = () => {
  const discardChangesWarning = useSelector(selectDiscardChangesWarning);

  const [modalOpen, setModalOpen] = useState(true);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const resetStoreValuesForNewRecord = useCallback(() => {
    dispatch(resetGeneralInfoForm());
    dispatch(resetSelectedApmFile());
    dispatch(resetSelectedAircraftDetails());
    dispatch(resetAPMSettingsStore());
    dispatch(resetSelectedApmRecord());
  }, [dispatch]);

  const onCloseHandler = () => {
    setModalOpen(false);
    dispatch(
      setDiscardChangesWarning({ showWarningModal: false, headerAction: null })
    );
  };

  const onContinueClickHandler = () => {
    switch (discardChangesWarning.headerAction) {
      case EnumHeaderAction.customerChange:
        navigate("/");
        dispatch(setShowAccountModal(true));
        onCloseHandler();
        break;
      case EnumHeaderAction.apmHistory:
        navigate("/");
        onCloseHandler();
        break;
      case EnumHeaderAction.createNewApm:
        resetStoreValuesForNewRecord();
        dispatch(setCurrentWorkFlowType(EnumApmSettingsWorkFlow.New));
        navigate("/new");
        dispatch(setCurrentWorkFlowStep(1));
        onCloseHandler();
        break;
    }
  };

  const getModalContent = (): ReactElement => {
    return (
      <p>
        Any changes made to the current record since the last save may be lost.
        Do you want to continue?
      </p>
    );
  };

  return (
    <Modal
      className="customer-change-warning-modal"
      size="large"
      closeIcon={true}
      closeOnDimmerClick={false}
      open={modalOpen}
      onClose={onCloseHandler}
    >
      <Modal.Header>Confirm</Modal.Header>
      <Modal.Content scrolling={false} className="modal-content">
        <Grid>
          <Grid.Row>
            <Grid.Column>{getModalContent()}</Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Modal.Footer>
        <Grid>
          <Grid.Row>
            <Grid.Column className="account-modal-footer-button-container">
              <Button
                type="secondary"
                size="medium"
                className="cancel-customer-popup-button"
                content="Cancel"
                onClick={onCloseHandler}
              />
              <Button
                type="primary"
                size="medium"
                content="Continue"
                className="select-customer-popup-button"
                onClick={onContinueClickHandler}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Footer>
    </Modal>
  );
};
